import {
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  forwardRef,
  useId,
} from 'react';

import { cn } from '@/lib/utils';

/**
 * V2 form-field primitives, lifted from the "Your details" screen of the
 * Gifting 1 – Web and Mobile OTC prototype: label above the control, 13px
 * radius, 1.5px stone border, green focus ring.
 *
 * Two non-default states:
 *  - `nudge` — the required-field nudge shown after pressing Continue with an
 *    empty required field (mint outline + soft pulse, no visible text — an
 *    sr-only "Required" is wired via aria-describedby instead).
 *  - `error` — an invalid value, with the message rendered inline beneath.
 */
export type FieldStatus = 'default' | 'nudge' | 'error';

const fieldControlClasses = (status: FieldStatus = 'default') =>
  cn(
    'block w-full rounded-[13px] border-[1.5px] border-[#8b8a88] bg-white px-[15px] py-[15px]',
    'text-base leading-snug text-[#1a201d] placeholder:text-[#6b736e]',
    'outline-none transition-[border-color,box-shadow] duration-150',
    'focus:border-[#256956] focus:ring-[3px] focus:ring-[#256956]/20',
    'disabled:bg-[#f8f7f7] disabled:text-[#8b8a88]',
    status === 'nudge' &&
      'border-[#66bda4] ring-4 ring-[#66bda4]/25 animate-[req-pulse_.5s_ease]',
    status === 'error' && 'border-[#f43f5e] bg-[#fff1f2]',
  );

type FieldShellProps = {
  id: string;
  label: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  /** Hint rendered between the label and the control (e.g. the phone field). */
  hintAbove?: ReactNode;
  error?: string;
  nudge?: boolean;
  className?: string;
  children: (a11y: {
    'aria-invalid': boolean | undefined;
    'aria-required': boolean | undefined;
    'aria-describedby': string | undefined;
  }) => ReactNode;
};

export function FieldShell({
  id,
  label,
  required,
  hint,
  hintAbove,
  error,
  nudge,
  className,
  children,
}: FieldShellProps) {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const nudgeId = `${id}-required`;

  const describedBy =
    [hint || hintAbove ? hintId : null, error ? errorId : null, nudge && !error ? nudgeId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div className={cn('text-left', className)} data-field={id}>
      <label
        htmlFor={id}
        className={cn(
          'block text-[13px] font-semibold text-[#2a2928]',
          hintAbove ? 'mb-0.5' : 'mb-1.5',
        )}
      >
        {label}
        {required && (
          <>
            <span aria-hidden='true'> *</span>
            <span className='sr-only'> (required)</span>
          </>
        )}
      </label>
      {hintAbove && (
        <p id={hintId} className='mx-0.5 mb-2 text-[12.5px] text-[#6b736e]'>
          {hintAbove}
        </p>
      )}
      {children({
        'aria-invalid': error || nudge ? true : undefined,
        'aria-required': required || undefined,
        'aria-describedby': describedBy,
      })}
      {error && (
        <p
          id={errorId}
          role='alert'
          data-testid={`${id}-error`}
          className='mx-0.5 mt-[7px] flex items-start gap-1.5 text-[12.5px] leading-[1.45] text-[#9f1239] animate-[err-in_.25s_ease]'
        >
          <svg
            width='14'
            height='14'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.2'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
            className='mt-[2px] shrink-0'
          >
            <circle cx='12' cy='12' r='10' />
            <path d='M12 8v4M12 16h.01' />
          </svg>
          <span>{error}</span>
        </p>
      )}
      {hint && !hintAbove && (
        <p id={hintId} className='mx-0.5 mt-[7px] text-[12.5px] text-[#6b736e]'>
          {hint}
        </p>
      )}
      {nudge && !error && (
        <span id={nudgeId} className='sr-only'>
          Required
        </span>
      )}
    </div>
  );
}

// ─── TextField ──────────────────────────────────────────────────────────────

export type TextFieldProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'className'
> & {
  id?: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  nudge?: boolean;
  className?: string;
  inputClassName?: string;
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(
    { id, label, hint, error, nudge, required, className, inputClassName, ...rest },
    ref,
  ) {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const status: FieldStatus = error ? 'error' : nudge ? 'nudge' : 'default';

    return (
      <FieldShell
        id={fieldId}
        label={label}
        required={required}
        hint={hint}
        error={error}
        nudge={nudge}
        className={className}
      >
        {(a11y) => (
          <input
            ref={ref}
            id={fieldId}
            className={cn(fieldControlClasses(status), inputClassName)}
            {...a11y}
            {...rest}
          />
        )}
      </FieldShell>
    );
  },
);

// ─── SelectField (native) ───────────────────────────────────────────────────

export type SelectOption = { value: string; label: string };

export type SelectFieldProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'id' | 'className' | 'children'
> & {
  id?: string;
  label: ReactNode;
  options: readonly SelectOption[];
  placeholder?: string;
  hint?: ReactNode;
  error?: string;
  nudge?: boolean;
  /** Current value — used to colour the placeholder state. */
  value?: string;
  className?: string;
};

export function Chevron({ className }: { className?: string }) {
  return (
    <svg
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='#6b736e'
      strokeWidth='2.4'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      className={className}
    >
      <path d='M6 9l6 6 6-6' />
    </svg>
  );
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  function SelectField(
    {
      id,
      label,
      options,
      placeholder = 'Select…',
      hint,
      error,
      nudge,
      required,
      value,
      className,
      ...rest
    },
    ref,
  ) {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const status: FieldStatus = error ? 'error' : nudge ? 'nudge' : 'default';
    const isEmpty = !value;

    return (
      <FieldShell
        id={fieldId}
        label={label}
        required={required}
        hint={hint}
        error={error}
        nudge={nudge}
        className={className}
      >
        {(a11y) => (
          <div className='relative'>
            <select
              ref={ref}
              id={fieldId}
              value={value}
              className={cn(
                fieldControlClasses(status),
                'appearance-none pr-11 font-medium',
                isEmpty && 'text-[#7a847d]',
              )}
              {...a11y}
              {...rest}
            >
              <option value=''>{placeholder}</option>
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <Chevron className='pointer-events-none absolute top-1/2 right-[15px] -translate-y-1/2' />
          </div>
        )}
      </FieldShell>
    );
  },
);
